import { useState, useMemo, useCallback, useRef } from 'react';
import { Button, Space, Switch, Tag, Typography, Alert } from 'antd';
import { SaveOutlined, ExperimentOutlined } from '@ant-design/icons';
import {
  pdfMapperToNormalized,
  normalizedToPdfMapper,
  normalizedToPixels,
  pixelsToNormalized,
  EditableBox,
  type NormalizedBox,
  type PdfPageGeometry,
} from '@ute/mapper-ui-components';
import { mappingApi } from '../../services/api/mapping';
import { TestPreviewModal } from './TestPreviewModal';

const { Text } = Typography;

interface TemplateField {
  fieldKey: string;
  norm: NormalizedBox;
  pattern?: string; // pattern-driven → non-draggable (D3)
  pageNo: number;   // 0-based (D8)
}

interface Args {
  mappingKey: string;
  pdfHash: string;
  templateData?: Record<string, any>;
  tenantId?: string;
  liveAdvancedValues?: Record<string, string | undefined>;
  onSaved?: () => void;
  /** Textract blocks + page dims from mappingData — used to seed a box from a pattern match. */
  blocks?: any[];
  pageWidth?: number;
  pageHeight?: number;
}

/**
 * Box-editing state shared between the Advanced tab (toggle + Test/Save + legend)
 * and the PDF overlay (draggable boxes). Boxes are stored normalized (0-1) so
 * they're zoom-independent; converted to/from pixels at the EditableBox boundary.
 */
export function useBoxEditor({
  mappingKey,
  pdfHash,
  templateData,
  tenantId,
  liveAdvancedValues,
  onSaved,
  blocks,
  pageWidth,
  pageHeight,
}: Args) {
  const [editEnabled, setEditEnabled] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  // When true, only the selected field box is shown (focus mode from FieldToolsPanel).
  // In focus mode, a null `selected` renders NO box (pattern mode hides the overlay).
  const [focusMode, setFocusMode] = useState(false);
  // Local boxes as normalized coords, keyed by fieldKey. Holds both auto-seeds
  // (from the highlight resolver) and manual drags.
  const [edits, setEdits] = useState<Record<string, NormalizedBox>>({});
  // Fields the user manually dragged — these are never auto-reseeded.
  const draggedRef = useRef<Set<string>>(new Set());
  // Page override per field (from the field panel's Page No dropdown), 0-based.
  const [pageOverrides, setPageOverrides] = useState<Record<string, number>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Candidate data passed to the test modal; null = modal closed
  const [testCandidate, setTestCandidate] = useState<Record<string, any> | null>(null);

  // Parse all coordinate fields from the saved template, plus any fields that
  // have a seeded edit (e.g. pattern→coords conversion) but no saved coords yet.
  const fields: TemplateField[] = useMemo(() => {
    const data = templateData ?? {};
    const out: TemplateField[] = [];
    const seen = new Set<string>();

    // 1. Fields that have saved coords in the template
    Object.keys(data).forEach((key) => {
      if (!key.endsWith('Coords')) return;
      const fieldKey = key.slice(0, -6);
      if (seen.has(fieldKey)) return;
      seen.add(fieldKey);
      const coords = data[key];
      if (!coords) return;
      out.push({
        fieldKey,
        norm: { x: 0, y: 0, w: 0, h: 0 }, // placeholder; resolved lazily in overlay
        pattern: data[`${fieldKey}Pattern`] ?? undefined,
        pageNo: pageOverrides[fieldKey] ?? parseInt(data[`${fieldKey}PageNo`] ?? '0', 10),
      });
    });

    // 2. Fields that have a local edit but no saved coords (e.g. seeded from pattern match)
    Object.keys(edits).forEach((fieldKey) => {
      if (seen.has(fieldKey)) return;
      seen.add(fieldKey);
      out.push({
        fieldKey,
        norm: edits[fieldKey],
        pattern: undefined, // switched to coords mode
        pageNo: pageOverrides[fieldKey] ?? parseInt(data[`${fieldKey}PageNo`] ?? '0', 10),
      });
    });

    return out;
  }, [templateData, edits, pageOverrides]);

  // Resolve the normalized box for a field: local edit wins, else parse template coords.
  const resolveNorm = useCallback(
    (fieldKey: string, pageW: number, pageH: number): NormalizedBox | null => {
      if (edits[fieldKey]) return edits[fieldKey];
      const coords = templateData?.[`${fieldKey}Coords`];
      return coords ? pdfMapperToNormalized(coords, pageW, pageH) : null;
    },
    [edits, templateData]
  );

  const buildPatch = useCallback(
    (pageW: number, pageH: number): Record<string, any> => {
      const patch: Record<string, any> = {};
      Object.keys(edits).forEach((fieldKey) => {
        const field = fields.find((f) => f.fieldKey === fieldKey);
        patch[`${fieldKey}Coords`] = normalizedToPdfMapper(edits[fieldKey], pageW, pageH);
        if (field) patch[`${fieldKey}PageNo`] = String(field.pageNo);
        // D4: setting coords clears the pattern (mutually exclusive)
        patch[`${fieldKey}Pattern`] = null;
      });
      return patch;
    },
    [edits, fields]
  );

  // ── Overlay render-prop for PdfViewer ─────────────────────────────────────
  const renderOverlay = useCallback(
    (g: PdfPageGeometry) => {
      if (!editEnabled) return null;
      // Focus mode (driven by a field's "Extract by" toggle): show ONLY the selected
      // field's box — and nothing when no field is selected (e.g. Pattern mode).
      const visibleFields = focusMode
        ? selected
          ? fields.filter((f) => f.fieldKey === selected)
          : []
        : fields;
      return (
        <>
          {visibleFields
            .filter((f) => f.pageNo === g.pageNumber - 1) // D8: only this page
            .map((f) => {
              const norm = resolveNorm(f.fieldKey, g.pageWidth, g.pageHeight);
              if (!norm) return null;
              const px = normalizedToPixels(norm, g.pageWidth, g.pageHeight, g.scale);
              return (
                <EditableBox
                  key={f.fieldKey}
                  fieldKey={f.fieldKey}
                  label={f.fieldKey}
                  box={px}
                  draggable={!f.pattern} // D3
                  selected={selected === f.fieldKey}
                  onSelect={setSelected}
                  onChange={(fk, box) => {
                    const n = pixelsToNormalized(box, g.pageWidth, g.pageHeight, g.scale);
                    draggedRef.current.add(fk); // user moved it — don't auto-reseed
                    setEdits((prev) => ({ ...prev, [fk]: n }));
                  }}
                />
              );
            })}
        </>
      );
    },
    [editEnabled, focusMode, fields, resolveNorm, selected]
  );

  // ── Test / Save (need page dims — captured from last overlay render) ──────
  const [geom, setGeom] = useState<PdfPageGeometry | null>(null);
  // Wrap renderOverlay to also capture geometry for Test/Save
  const renderOverlayCapturing = useCallback(
    (g: PdfPageGeometry) => {
      if (geom?.pageWidth !== g.pageWidth || geom?.pageHeight !== g.pageHeight) {
        // capture latest geometry (deferred to avoid render-phase setState)
        queueMicrotask(() => setGeom(g));
      }
      return renderOverlay(g);
    },
    [renderOverlay, geom]
  );

  // Open the shared Test modal with the current candidate (template + advanced + box edits)
  const handleTest = () => {
    if (!geom) return;
    setError(null);
    setTestCandidate({
      ...(templateData ?? {}),
      ...(liveAdvancedValues ?? {}),
      ...buildPatch(geom.pageWidth, geom.pageHeight),
    });
  };

  const handleSave = async () => {
    if (!geom) return;
    setSaving(true);
    setError(null);
    try {
      const saveData = {
        ...(liveAdvancedValues ?? {}),
        ...buildPatch(geom.pageWidth, geom.pageHeight),
      };
      await mappingApi.updateTemplate(mappingKey, saveData, tenantId);
      setEdits({}); // committed
      draggedRef.current.clear();
      setPageOverrides({});
      onSaved?.();
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError('Rejected: changes would break previously verified data.');
      } else {
        setError(err?.response?.data?.message || 'Save failed');
      }
    } finally {
      setSaving(false);
    }
  };

  // Resolve the best seed box for a field, in priority order:
  //   1. caller-supplied box (the highlight resolver — tracks current value/selection)
  //   2. saved template coords
  //   3. pattern → first matching Textract block
  //   4. default center box (so there's always something to drag)
  const resolveSeed = useCallback(
    (fieldKey: string, seedBox?: NormalizedBox | null): NormalizedBox => {
      if (seedBox) return seedBox;

      const coords = templateData?.[`${fieldKey}Coords`];
      if (coords && pageWidth && pageHeight) {
        const n = pdfMapperToNormalized(coords, pageWidth, pageHeight);
        if (n) return n;
      }

      const pattern = templateData?.[`${fieldKey}Pattern`];
      if (pattern && blocks?.length) {
        try {
          let flags = 'gmi';
          let cleanPattern = pattern;
          if (pattern.startsWith('(?i)')) { cleanPattern = pattern.slice(4); }
          else if (pattern.startsWith('(?mi)') || pattern.startsWith('(?im)')) { cleanPattern = pattern.slice(5); }
          else { flags = 'gm'; }
          const re = new RegExp(cleanPattern, flags);
          const matchingBlock = blocks.find(
            (b: any) =>
              (b.BlockType === 'LINE' || b.BlockType === 'WORD') &&
              b.Text &&
              re.test(b.Text) &&
              b.Geometry?.BoundingBox
          );
          if (matchingBlock) {
            const bb = matchingBlock.Geometry.BoundingBox;
            return { x: bb.Left, y: bb.Top, w: bb.Width, h: bb.Height };
          }
        } catch {
          // invalid regex — fall through to default
        }
      }

      return { x: 0.35, y: 0.4, w: 0.2, h: 0.05 };
    },
    [templateData, blocks, pageWidth, pageHeight]
  );

  // Activate box-edit + focus mode for a field (called when its "Extract by" toggle
  // is set to Coordinates). Seeds a draggable box from the current value/selection so
  // it's consistent with the highlight. Re-seeds on every call UNLESS the user has
  // manually dragged this field's box (those stay put).
  const activateField = useCallback(
    (fieldKey: string, seedBox?: NormalizedBox | null) => {
      setEditEnabled(true);
      setSelected(fieldKey);
      setFocusMode(true);

      // Manual drag wins — never auto-reseed a box the user positioned.
      if (draggedRef.current.has(fieldKey)) return;

      const seed = resolveSeed(fieldKey, seedBox);
      setEdits((prev) => ({ ...prev, [fieldKey]: seed }));
    },
    [resolveSeed]
  );

  // Deactivate focus mode (called when the toggle leaves Coordinates, e.g. → Pattern).
  // Clears the selection so the overlay renders no box for this field.
  const deactivateField = useCallback((fieldKey: string) => {
    setSelected((cur) => (cur === fieldKey ? null : cur));
  }, []);

  // Move a field's box to a different page (from the field panel's Page No dropdown).
  const setFieldPage = useCallback((fieldKey: string, pageNo: number) => {
    setPageOverrides((prev) =>
      prev[fieldKey] === pageNo ? prev : { ...prev, [fieldKey]: pageNo }
    );
  }, []);

  // Discard all uncommitted edits for a field (drag, page override) and deactivate it.
  // Called when the user cancels editing — the box reverts to the saved template state.
  // If editing was driven by the field toggle (focus mode), also turn the PDF edit mode
  // back off so the overlay returns to its idle state.
  const discardField = useCallback((fieldKey: string) => {
    draggedRef.current.delete(fieldKey);
    setEdits((prev) => {
      if (!(fieldKey in prev)) return prev;
      const { [fieldKey]: _drop, ...rest } = prev;
      return rest;
    });
    setPageOverrides((prev) => {
      if (!(fieldKey in prev)) return prev;
      const { [fieldKey]: _drop, ...rest } = prev;
      return rest;
    });
    setSelected((cur) => (cur === fieldKey ? null : cur));
    if (focusMode) {
      setFocusMode(false);
      setEditEnabled(false);
    }
  }, [focusMode]);

  const dirtyCount = Object.keys(edits).length;

  // ── Controls for the Advanced tab ─────────────────────────────────────────
  const controls = (
    <div style={{ marginBottom: 16 }}>
      <Space align="center" style={{ marginBottom: 8 }}>
        <Switch checked={editEnabled} onChange={(v) => { setEditEnabled(v); if (!v) setFocusMode(false); }} size="small" />
        <Text strong style={{ fontSize: 13 }}>Edit field boxes on the PDF</Text>
      </Space>

      {editEnabled && (
        <>
          <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>
            Drag boxes on the PDF to reposition. Pattern fields can't be moved —
            edit their pattern instead.
          </div>

          {fields.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {fields.map((f) => (
                <Tag
                  key={f.fieldKey}
                  color={f.pattern ? 'purple' : edits[f.fieldKey] ? 'orange' : selected === f.fieldKey ? 'blue' : 'default'}
                  style={{ cursor: 'pointer', fontSize: 11 }}
                  onClick={() => setSelected(f.fieldKey)}
                >
                  {f.pattern ? '⌖ ' : '⊞ '}{f.fieldKey}
                  {edits[f.fieldKey] && ' *'}
                </Tag>
              ))}
            </div>
          )}

          {error && <Alert type="error" message={error} showIcon closable onClose={() => setError(null)} style={{ marginBottom: 8 }} />}

          <Space style={{ marginBottom: 8 }}>
            <Button size="small" icon={<ExperimentOutlined />} disabled={dirtyCount === 0} onClick={handleTest}>
              Test ({dirtyCount} changed)
            </Button>
            <Button size="small" type="primary" icon={<SaveOutlined />} loading={saving} disabled={dirtyCount === 0} onClick={handleSave}>
              Save to Template
            </Button>
          </Space>
        </>
      )}

      {/* Test result modal — shows extracted data for the candidate */}
      <TestPreviewModal
        mappingKey={mappingKey}
        pdfHash={pdfHash}
        tenantId={tenantId}
        candidateData={testCandidate ?? undefined}
        open={testCandidate !== null}
        onClose={() => setTestCandidate(null)}
      />
    </div>
  );

  // Returns the current dragged box as a pdfmapper coords string using captured geometry.
  // Used by FieldToolsPanel to include the dragged box in test/save patches.
  const getFieldEdit = useCallback(
    (fieldKey: string, pw: number, ph: number): string | null => {
      const norm = edits[fieldKey];
      if (!norm) return null;
      return normalizedToPdfMapper(norm, pw, ph);
    },
    [edits]
  );

  // Convenience: uses already-captured geometry so FieldToolsPanel doesn't need dims.
  const getFieldCoords = useCallback(
    (fieldKey: string): string | null => {
      if (!geom) return null;
      return getFieldEdit(fieldKey, geom.pageWidth, geom.pageHeight);
    },
    [geom, getFieldEdit]
  );

  return { renderOverlay: renderOverlayCapturing, controls, editEnabled, focusMode, activateField, deactivateField, discardField, setFieldPage, getFieldEdit, getFieldCoords };
}
